<template>
  <div>
    <b-modal
      id="businessBasicAgreementModal"
      modal-class="business-basic-agreement-modal-cover"
      content-class="business-basic-agreement-modal-content"
      v-model="isVisible"
      hide-header
      hide-footer
      size="xl"
      scrollable
      centered
      body-class="business-basic-agreement-body-outer p-0"
      @hidden="onHidden"
    >
      <div class="contract-shell">
        <header class="contract-hero" :dir="isHebrew ? 'rtl' : 'ltr'">
          <div class="contract-hero-accent" aria-hidden="true" />
          <div class="contract-hero-inner">
            <p class="contract-hero-eyebrow">{{ t("businessBasicAgreementHeroEyebrow") }}</p>
            <h2 id="business-basic-agreement-heading" class="contract-hero-title">
              {{ t("businessBasicAgreementModalTitle") }}
            </h2>
            <p class="contract-hero-meta">
              <span class="contract-hero-version">{{ t("businessBasicAgreementVersionLabel") }}</span>
              <span class="contract-hero-dot" aria-hidden="true">·</span>
              <span class="contract-hero-hint">{{ t("businessBasicAgreementHeroHint") }}</span>
            </p>
          </div>
        </header>

        <div class="contract-body-wrap" :dir="isHebrew ? 'rtl' : 'ltr'">
          <div class="contract-paper">
            <div class="contract-paper-inner-border">
              <article class="contract-prose" aria-labelledby="business-basic-agreement-heading">
                <h3 class="contract-doc-title">{{ agreementDoc.docTitle }}</h3>
                <p class="contract-intro">{{ agreementDoc.intro }}</p>
                <section
                  v-for="(section, si) in agreementDoc.sections"
                  :key="'sec-' + si"
                  class="contract-section"
                >
                  <h4 class="contract-section-title">{{ section.title }}</h4>
                  <template v-for="(block, bi) in section.blocks">
                    <p
                      v-if="block.type === 'p'"
                      :key="'p-' + si + '-' + bi"
                      class="contract-p"
                    >
                      {{ block.text }}
                    </p>
                    <ul
                      v-else-if="block.type === 'ul'"
                      :key="'ul-' + si + '-' + bi"
                      class="contract-list"
                    >
                      <li v-for="(item, ii) in block.items" :key="ii" class="contract-list-item">
                        {{ item }}
                      </li>
                    </ul>
                    <div v-else-if="block.type === 'table'" :key="'tb-' + si + '-' + bi" class="contract-table-wrap" dir="auto">
                      <table class="contract-table">
                        <thead>
                          <tr><th v-for="(h, hi) in block.headers" :key="hi">{{ h }}</th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="(row, ri) in block.rows" :key="ri">
                            <td v-for="(cell, ci) in row" :key="ci">{{ cell }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div
                      v-else-if="block.type === 'stack'"
                      :key="'st-' + si + '-' + bi"
                      class="contract-subsection"
                    >
                      <h5 class="contract-subsection-title">{{ block.title }}</h5>
                      <div
                        v-for="(b2, bj) in block.blocks"
                        :key="'sb-' + si + '-' + bi + '-' + bj"
                        class="contract-subsection-block"
                      >
                        <p v-if="b2.type === 'p'" class="contract-p">
                          {{ b2.text }}
                        </p>
                        <ul
                          v-else-if="b2.type === 'ul'"
                          class="contract-list contract-list--nested"
                        >
                          <li v-for="(item, ii) in b2.items" :key="ii" class="contract-list-item">
                            {{ item }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </template>
                </section>
              </article>
            </div>
          </div>
        </div>

        <div
          class="contract-phone-wrap"
          ref="phoneSection"
          :dir="isHebrew ? 'rtl' : 'ltr'"
          :lang="isHebrew ? 'he' : 'en'"
        >
          <label class="contract-phone-label" for="business-basic-agreement-phone">
            {{ t("businessBasicAgreementPhoneLabel") }}
          </label>
          <input
            id="business-basic-agreement-phone"
            v-model="agreementPhone"
            ref="phoneInput"
            type="tel"
            inputmode="tel"
            class="contract-phone-input"
            :disabled="isSubmitting"
            autocomplete="tel"
            :placeholder="t('businessBasicAgreementPhonePlaceholder')"
            :aria-label="t('businessBasicAgreementPhoneLabel')"
            @blur="phoneBlurred = true"
          />
          <p v-if="phoneHintVisible" class="contract-phone-hint">{{ t("businessBasicAgreementPhoneHint") }}</p>
          <p v-if="phoneErrorText" class="contract-phone-error" role="alert">{{ phoneErrorText }}</p>
        </div>

        <div class="contract-ack" ref="ackSection" :dir="isHebrew ? 'rtl' : 'ltr'">
          <h4 class="contract-ack-title">{{ t("businessBasicAgreementAckTitle") }}</h4>
          <label class="contract-ack-label">
            <input
              v-model="accepted"
              type="checkbox"
              class="contract-ack-input"
              :disabled="isSubmitting"
            />
            <span class="contract-ack-copy">{{ t("businessBasicAgreementCheckbox") }}</span>
          </label>
          <label class="contract-ack-label">
            <input
              v-model="acceptedAi"
              type="checkbox"
              class="contract-ack-input"
              :disabled="isSubmitting"
            />
            <span class="contract-ack-copy">{{ t("businessBasicAgreementAiCheckbox") }}</span>
          </label>
          <p v-if="ackErrorText" class="contract-ack-error" role="alert">{{ ackErrorText }}</p>
        </div>

        <footer class="contract-footer" :dir="isHebrew ? 'rtl' : 'ltr'">
          <button
            type="button"
            class="contract-btn contract-btn-secondary"
            :disabled="isSubmitting"
            @click="closeModal"
          >
            {{ t("businessBasicAgreementClose") }}
          </button>
          <button
            type="button"
            class="contract-btn contract-btn-primary"
            :disabled="isSubmitting"
            @click="onAcceptClick"
          >
            <span v-if="isSubmitting" class="contract-btn-spinner spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            {{ t("businessBasicAgreementAccept") }}
          </button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { translationMixin } from "@/mixins/translationMixin";
import { getBusinessBasicAgreementByRegion } from "@/content/saasBusinessBasicAgreementText";
import { isValidContactPhone } from "@/common/validateContactPhone";

export default {
  name: "SaasBusinessBasicAgreementModal",
  props: ["value"],
  mixins: [translationMixin],
  data() {
    return {
      accepted: false,
      acceptedAi: false,
      isSubmitting: false,
      agreementPhone: "",
      phoneBlurred: false,
      phoneSubmitAttempted: false,
      ackSubmitAttempted: false,
    };
  },
  computed: {
    phoneValid() {
      return isValidContactPhone(this.agreementPhone);
    },
    phoneHintVisible() {
      return !this.phoneValid && !this.phoneErrorText;
    },
    phoneErrorText() {
      const p = (this.agreementPhone || "").trim();
      if (!this.phoneBlurred && !this.phoneSubmitAttempted) {
        return "";
      }
      if (!p) {
        return this.t("businessBasicAgreementPhoneRequired");
      }
      if (!isValidContactPhone(p)) {
        return this.t("businessBasicAgreementPhoneInvalid");
      }
      return "";
    },
    agreementDoc() {
      return getBusinessBasicAgreementByRegion(this.$store.state.regionConfig.currency);
    },
    allAcksChecked() {
      return this.accepted && this.acceptedAi;
    },
    ackErrorText() {
      if (!this.ackSubmitAttempted || this.allAcksChecked) {
        return "";
      }
      return this.t("businessBasicAgreementCheckboxRequired");
    },
    isVisible: {
      set(val) {
        this.$emit("input", val);
      },
      get() {
        return this.value;
      },
    },
  },
  watch: {
    isVisible(val) {
      if (val) {
        this.accepted = false;
        this.acceptedAi = false;
        this.isSubmitting = false;
        this.agreementPhone = "";
        this.phoneBlurred = false;
        this.phoneSubmitAttempted = false;
        this.ackSubmitAttempted = false;
      }
    },
  },
  methods: {
    ...mapActions(["acceptBisnessBasicAgreementAndBecomeRideManager"]),
    scrollToSection(refName) {
      const el = this.$refs[refName];
      if (!el || typeof el.scrollIntoView !== "function") {
        return;
      }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    closeModal() {
      this.isVisible = false;
    },
    async onAcceptClick() {
      if (this.isSubmitting) {
        return;
      }
      this.phoneSubmitAttempted = true;
      this.ackSubmitAttempted = true;
      if (!this.allAcksChecked) {
        this.scrollToSection("ackSection");
        return;
      }
      if (!this.phoneValid) {
        this.phoneBlurred = true;
        this.scrollToSection("phoneSection");
        this.$nextTick(() => {
          if (this.$refs.phoneInput && typeof this.$refs.phoneInput.focus === "function") {
            this.$refs.phoneInput.focus();
          }
        });
        return;
      }
      this.isSubmitting = true;
      try {
        await this.acceptBisnessBasicAgreementAndBecomeRideManager({
          phone: (this.agreementPhone || "").trim(),
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    onHidden() {
      if (this.$store.state.appauth.businessBasicAgreementFlow) {
        this.$store.commit("setBusinessBasicAgreementFlow", null);
      }
      if (this.$store.state.appauth.BisnessBasicAgreementShareGateArmed) {
        this.$store.commit("setBisnessBasicAgreementShareGateArmed", false);
      }
      this.accepted = false;
      this.acceptedAi = false;
      this.isSubmitting = false;
      this.ackSubmitAttempted = false;
    },
  },
};
</script>

<style scoped>
.contract-shell {
  display: flex;
  flex-direction: column;
  /* Size to content so modal-body can scroll the full column on short viewports */
  flex: 0 0 auto;
  width: 100%;
}

.contract-hero {
  position: relative;
  flex-shrink: 0;
  padding: 1.75rem 1.75rem 1.35rem;
  background: linear-gradient(165deg, #141c2b 0%, #0a0f18 48%, #111827 100%);
  color: #f8fafc;
  text-align: center;
  overflow: hidden;
}

.contract-hero::after {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 80% 50% at 50% -20%, rgba(212, 175, 106, 0.15), transparent 55%);
  pointer-events: none;
}

.contract-hero-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #8b7355, #d4af6e, #c9a227, #d4af6e, #8b7355);
}

.contract-hero-inner {
  position: relative;
  z-index: 1;
}

.contract-hero-eyebrow {
  font-size: 0.72rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(212, 175, 106, 0.95);
  margin-bottom: 0.65rem;
  font-weight: 600;
}

.contract-hero-title {
  font-family: "Frank Ruhl Libre", "Times New Roman", Georgia, serif;
  font-size: clamp(1.35rem, 2.8vw, 1.85rem);
  font-weight: 700;
  line-height: 1.35;
  margin: 0 0 0.75rem;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}

.contract-hero-meta {
  margin: 0;
  font-size: 0.88rem;
  color: rgba(226, 232, 240, 0.88);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.35rem 0.5rem;
}

.contract-hero-version {
  font-weight: 600;
  color: #e2c77a;
}

.contract-hero-dot {
  opacity: 0.55;
}

.contract-hero-hint {
  opacity: 0.9;
  font-size: 0.82rem;
}

.contract-body-wrap {
  flex: 0 0 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: visible;
  padding: 1.25rem 1.25rem 0.75rem;
  background: linear-gradient(180deg, #e8e4dc 0%, #d4cfc4 100%);
}

.contract-paper {
  flex: 0 0 auto;
  min-height: auto;
  max-height: none;
  overflow: visible;
  padding: 2px;
  background: linear-gradient(145deg, #c9a22733, #1e293b22);
  border-radius: 4px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.55) inset,
    0 12px 40px rgba(15, 23, 42, 0.18),
    0 2px 8px rgba(15, 23, 42, 0.08);
}

.contract-paper-inner-border {
  background: #fdfbf7;
  border: 1px solid rgba(139, 115, 85, 0.35);
  outline: 1px solid rgba(255, 255, 255, 0.65);
  outline-offset: -3px;
  border-radius: 2px;
  padding: 1.35rem 1.5rem 1.5rem;
  min-height: 120px;
}

.contract-prose {
  font-family: "Frank Ruhl Libre", "David Libre", "Times New Roman", Georgia, serif;
  font-size: 1.02rem;
  line-height: 1.7;
  color: #1a1a1a;
  margin: 0;
  font-weight: 400;
  letter-spacing: 0.01em;
}

.contract-doc-title {
  font-family: "Frank Ruhl Libre", "Times New Roman", Georgia, serif;
  font-size: 1.32rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.85rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(201, 162, 39, 0.5);
  letter-spacing: 0.02em;
  line-height: 1.35;
}

.contract-intro {
  margin: 0 0 1.35rem;
  color: #334155;
  line-height: 1.75;
  font-size: 1rem;
}

.contract-section {
  margin: 0;
  padding: 1.1rem 0 1.2rem;
  border-top: 1px solid rgba(139, 115, 85, 0.22);
}

.contract-section:first-of-type {
  border-top: none;
  padding-top: 0.2rem;
}

.contract-section-title {
  font-family: "Frank Ruhl Libre", "Times New Roman", Georgia, serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.7rem;
  padding: 0.4rem 0.75rem;
  background: rgba(201, 162, 39, 0.11);
  border-inline-start: 4px solid #a67c00;
  border-radius: 4px;
  line-height: 1.4;
}

.contract-subsection {
  margin: 0.75rem 0 0.95rem;
  padding: 0.65rem 0.85rem 0.75rem;
  background: rgba(255, 255, 255, 0.72);
  border-radius: 6px;
  border: 1px solid rgba(139, 115, 85, 0.18);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.contract-subsection:last-child {
  margin-bottom: 0;
}

.contract-subsection-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #475569;
  margin: 0 0 0.45rem;
  letter-spacing: 0.02em;
}

.contract-subsection-block:last-child .contract-p:last-child,
.contract-subsection-block:last-child .contract-list:last-child {
  margin-bottom: 0;
}

.contract-p {
  margin: 0 0 0.65rem;
}

.contract-list {
  margin: 0 0 0.75rem;
  padding-inline-start: 1.25rem;
  list-style-type: disc;
}

.contract-list:last-child {
  margin-bottom: 0;
}

.contract-list--nested {
  margin-bottom: 0.45rem;
}

.contract-list-item {
  margin-bottom: 0.32rem;
  padding-inline-start: 0.2rem;
}

.contract-table-wrap { overflow-x: auto; margin: 0 0 0.75rem; }
.contract-table { width: 100%; border-collapse: collapse; font-size: 0.92em; }
.contract-table th,
.contract-table td {
  border: 1px solid rgba(139, 115, 85, 0.3); padding: 0.4em 0.6em;
  text-align: center; vertical-align: middle;
}
.contract-table th { background: rgba(201, 162, 39, 0.11); font-weight: 700; color: #0f172a; }
.contract-table th:first-child,
.contract-table td:first-child { text-align: start; font-weight: 600; }

.contract-list-item::marker {
  color: #0f766e;
}

.contract-phone-wrap {
  flex-shrink: 0;
  padding: 0.85rem 1.5rem 0.5rem;
  background: #f4f1eb;
  border-top: 1px solid rgba(139, 115, 85, 0.2);
}

.contract-phone-wrap[dir="rtl"] {
  text-align: right;
  unicode-bidi: isolate;
}

.contract-phone-wrap[dir="ltr"] {
  text-align: left;
  unicode-bidi: isolate;
}

.contract-phone-label {
  display: block;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.4rem;
}

.contract-phone-input {
  width: 100%;
  max-width: 22rem;
  padding: 0.55rem 0.75rem;
  font-size: 1.05rem;
  border: 1px solid rgba(139, 115, 85, 0.35);
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  /* Phone numbers and + prefix read left-to-right even in Hebrew UI */
  direction: ltr;
  text-align: left;
}

.contract-phone-input:focus {
  outline: none;
  border-color: #0f766e;
  box-shadow: 0 0 0 2px rgba(15, 118, 110, 0.2);
}

.contract-phone-input:disabled {
  opacity: 0.65;
}

.contract-phone-hint {
  margin: 0.35rem 0 0;
  font-size: 0.82rem;
  color: #64748b;
}

.contract-phone-error {
  margin: 0.35rem 0 0;
  font-size: 0.88rem;
  color: #b91c1c;
}

.contract-ack {
  flex-shrink: 0;
  padding: 1rem 1.5rem 0.25rem;
  background: #f4f1eb;
  border-top: 1px solid rgba(139, 115, 85, 0.2);
}

.contract-ack-title {
  margin: 0 0 0.6rem;
  text-align: start;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
}

.contract-ack-label + .contract-ack-label {
  margin-top: 0.55rem;
}

.contract-ack-label {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  cursor: pointer;
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.5;
}

.contract-ack-input {
  margin-top: 0.35rem;
  width: 1.15rem;
  height: 1.15rem;
  flex-shrink: 0;
  accent-color: #0f766e;
}

.contract-ack-copy {
  flex: 1;
}

.contract-ack-error {
  margin: 0.45rem 0 0;
  font-size: 0.88rem;
  color: #b91c1c;
}

.contract-footer {
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  z-index: 6;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: space-between;
  align-items: center;
  padding: 1.15rem 1.5rem 1.35rem;
  background: linear-gradient(180deg, #f8f6f2 0%, #f0ebe3 100%);
  border-top: 1px solid rgba(139, 115, 85, 0.25);
  box-shadow: 0 -8px 18px rgba(15, 23, 42, 0.14);
}

.contract-btn {
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.65rem 1.5rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  min-width: 8rem;
}

.contract-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.contract-btn-secondary {
  background: #fff;
  color: #334155;
  border: 1px solid #cbd5e1;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.contract-btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}

.contract-btn-primary {
  background: linear-gradient(180deg, #0d9488 0%, #0f766e 45%, #0d5c54 100%);
  color: #fff;
  box-shadow:
    0 2px 4px rgba(15, 118, 110, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.contract-btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow:
    0 4px 12px rgba(15, 118, 110, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.contract-btn-spinner {
  vertical-align: -0.15em;
  margin-inline-end: 0.4rem;
}

@media (max-width: 576px) {
  .contract-hero {
    padding: 1.35rem 1rem 1.1rem;
  }

  .contract-prose {
    font-size: 0.95rem;
  }

  .contract-doc-title {
    font-size: 1.15rem;
  }

  .contract-footer {
    flex-direction: column-reverse;
  }

  .contract-btn {
    width: 100%;
  }
}

/* Short viewports: smaller agreement block so phone + actions stay reachable via outer scroll */
@media (max-height: 700px) {
  .contract-hero {
    padding: 1rem 1rem 0.85rem;
  }

  .contract-phone-wrap,
  .contract-ack {
    padding-top: 0.65rem;
    padding-bottom: 0.4rem;
  }

  .contract-footer {
    padding: 0.85rem 1.25rem 1rem;
  }
}

/* Hebrew (RTL): align copy to the right; hero overrides center alignment */
.contract-hero[dir="rtl"] {
  text-align: start;
}

.contract-hero[dir="rtl"] .contract-hero-meta {
  justify-content: flex-start;
}

.contract-body-wrap[dir="rtl"] .contract-prose {
  text-align: start;
}

.contract-ack[dir="rtl"] .contract-ack-label {
  text-align: start;
}
</style>

<style>
@import url("https://fonts.googleapis.com/css2?family=Frank+Ruhl+Libre:wght@400;500;600;700&display=swap");

.business-basic-agreement-modal-cover .modal-dialog {
  max-width: min(920px, 96vw) !important;
  max-height: min(calc(100vh - 1.5rem), calc(100dvh - 1.5rem));
  margin: 0.75rem auto;
}

.business-basic-agreement-modal-content {
  border: none;
  border-radius: 8px;
  overflow: hidden;
  max-height: min(92vh, 900px);
  max-height: min(92dvh, 900px);
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-shadow:
    0 25px 80px rgba(15, 23, 42, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.06) inset;
}

.business-basic-agreement-modal-cover .modal-body.business-basic-agreement-body-outer {
  padding: 0 !important;
  flex: 1 1 auto;
  min-height: 0;
  display: flex !important;
  flex-direction: column;
  /* Definite cap so overflow-y scrolls; phone + checkbox + footer stay reachable */
  max-height: min(85vh, 820px);
  max-height: min(85dvh, 820px);
  /* Allow vertical scroll so phone + checkbox + buttons stay reachable on low-height screens */
  overflow-y: auto !important;
  overflow-x: hidden !important;
  -webkit-overflow-scrolling: touch;
}

.business-basic-agreement-modal-cover.modal .modal-content {
  background: transparent;
}
</style>
